import React, { useEffect, useState } from "react"
import styles from "./DaySelector.module.scss"

import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { TableListButton } from "../Misc/Buttons/TableListButton"

const FIRST_DAY_OF_WEEK = 1 // 0 = Sunday, 1 = Monday, 2 = Tuesday, ...

function getWeekdays(firstDayOfWeek: number, t: TFunction) {
  const days = [
    t("_sunday"),
    t("_monday"),
    t("_tuesday"),
    t("_wednesday"),
    t("_thursday"),
    t("_friday"),
    t("_saturday"),
  ]
  return days.slice(firstDayOfWeek).concat(days.slice(0, firstDayOfWeek))
}

function getMonthAndYear(month: number, year: number, t: TFunction) {
  const months = [
    t("January {{year}}", {year: year}),
    t("February {{year}}", {year: year}),
    t("March {{year}}", {year: year}),
    t("April {{year}}", {year: year}),
    t("May {{year}}", {year: year}),
    t("June {{year}}", {year: year}),
    t("July {{year}}", {year: year}),
    t("August {{year}}", {year: year}),
    t("September {{year}}", {year: year}),
    t("October {{year}}", {year: year}),
    t("November {{year}}", {year: year}),
    t("December {{year}}", {year: year}),
  ]
  return months[month]
}

function getDays(year: number, month: number, firstDayOfWeek: number): Array<Date> {
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells = []
  const firstDay = (firstDayOfMonth - firstDayOfWeek + 7) % 7

  let i = 0 - firstDay
  while (i < daysInMonth || (i + firstDay) % 7 !== 0) {
    cells.push(new Date(year, month, i + 1))
    i++
  }
  return cells
}

interface Props {
  /** Date used to select initial month and year */
  date: Date
  /** Minimum date that can be navigated to and selected */
  min?: Date
  /** Maximum date that can be navigated to and selected */
  max?: Date
  /**
   * Indicate whether day is selectable, all days are selectable if not provided
   * but does not override `min` and `max` if they are provided
   */
  daySelectable?: (date: Date) => boolean
  /** Indicate whether day is selected for styling purposes */
  daySelected?: (date: Date) => boolean
  /** Set custom CSS properties for day cell */
  dayStyle?: (date: Date) => React.CSSProperties | undefined
  /** Callback that is triggered when user selects a day */
  onSelect: (date: Date) => void
}

/** Date selection component used in Date Picker components */
export function DaySelector({
  date,
  min,
  max,
  daySelectable,
  daySelected,
  dayStyle,
  onSelect,
}: Props) {
  const { t } = useTranslation()
  const [month, setMonth] = useState(date.getMonth())
  const [year, setYear] = useState(date.getFullYear())

  useEffect(() => {
    setMonth(date.getMonth())
    setYear(date.getFullYear())
  }, [date])

  // Prepare clean dates for comparisons
  const minDay = min ? new Date(min.getFullYear(), min.getMonth(), min.getDate()) : undefined
  const maxDay = max ? new Date(max.getFullYear(), max.getMonth(), max.getDate()) : undefined
  const minMonth = min ? new Date(min.getFullYear(), min.getMonth()) : undefined
  const maxMonth = max ? new Date(max.getFullYear(), max.getMonth()) : undefined

  return <div className={styles.container}>
    <div className={styles["month-selector"]}>
      <TableListButton
        title={t("Previous year")}
        disabled={minMonth && minMonth >= new Date(year, month)}
        onClick={() => {
          if (minMonth && minMonth >= new Date(year - 1, month)){
            setYear(minMonth.getFullYear())
            setMonth(minMonth.getMonth())
          }
          else {
            setYear(year - 1)
          }
        }}
      >
        <i className="fa fa-angles-left"/>
      </TableListButton>
      <TableListButton
        title={t("Previous month")}
        disabled={minMonth && minMonth >= new Date(year, month)}
        onClick={() => {
          setYear(month === 0 ? year -1 : year)
          setMonth(month === 0 ? 11 : month - 1)
        }}
      >
        <i className="fa fa-angle-left"/>
      </TableListButton>
      <span>
        {getMonthAndYear(month, year, t)}
      </span>
      <TableListButton
        title={t("Next month")}
        disabled={maxMonth && maxMonth <= new Date(year, month)}
        onClick={() => {
          setYear(month === 11 ? year + 1 : year)
          setMonth(month === 11 ? 0 : month + 1)
        }}
      >
        <i className="fa fa-angle-right"/>
      </TableListButton>
      <TableListButton
        title={t("Next year")}
        disabled={maxMonth && maxMonth <= new Date(year, month)}
        onClick={() => {
          if (maxMonth && maxMonth <= new Date(year + 1, month)){
            setYear(maxMonth.getFullYear())
            setMonth(maxMonth.getMonth())
          }
          else {
            setYear(year + 1)
          }
        }}
      >
        <i className="fa fa-angles-right"/>
      </TableListButton>
    </div>
    <br/>
    <div className={styles.calendar}>
      {getWeekdays(FIRST_DAY_OF_WEEK, t).map((day, index) =>
        <div key={index} className={styles.weekday}>
          {day}
        </div>)
      }
    </div>
    <hr/>
    <div className={styles.calendar}>
      {getDays(year, month, FIRST_DAY_OF_WEEK).map(day => {
        if (month !== day.getMonth()) return <div key={day.getTime()}/>
        // Determine if this day is selectable
        let selectable = daySelectable ? daySelectable(day) : true
        if (minDay && day < minDay) selectable = false
        if (maxDay && day > maxDay) selectable = false

        // Add styles for the day
        const classes = []
        classes.push(styles.day)
        if (selectable) classes.push(styles.selectable)
        if (selectable && daySelected && daySelected(day)) classes.push(styles.selected)

        return <div
          key={day.getTime()}
          onClick={() => selectable && onSelect(day)}
          className={classes.join(" ")}
          style={dayStyle && dayStyle(day)}
        >
          {day.getDate()}
        </div>
      }
      )}
    </div>
  </div>
}